/**
 * Conexão com o banco `BF_SELLER_DATASETS` — o banco chave-valor dos datasets.
 *
 * banco antigo sai de `package.json` (`Number('1.4.1055'.replace('.', ''))` → `14.1055`, que o
 * IndexedDB trunca para `14`), e uma versão que diminui derruba o banco inteiro no `catch`.
 *
 * Os nomes de banco e de store aqui são literais, sem passar por `toCoded`: durante a migração
 * poder abrir o DevTools e ler `BF_SELLER_DATASETS` vale mais do que a ofuscação (que é base64, não
 * segurança). Se a decisão for manter a convenção antiga, é só envolver as duas constantes.
 */

import { openDB, deleteDB, IDBPDatabase } from 'idb';
import { BF_SELLER_STORES } from './bfSellerSchema';

export const BF_SELLER_DB_NAME = 'BF_SELLER_DATASETS';

/**
 * Suba esta versão sempre que o **formato das linhas** mudar — o upgrade recria as stores vazias e
 * o `__meta` vai junto, então `hasDatasetRows` responde `false` e o `_useGetLinks` baixa tudo de
 * novo. Sem isso, o app leria linha nova com leitor velho (ou o contrário) e mostraria dado errado.
 *
 * v3: um registro por arquivo em todas as stores, com a chave tirada do caminho da URL.
 */
export const BF_SELLER_DB_VERSION = 3;
export const BF_SELLER_META_STORE = '__meta';

let connection: Promise<IDBPDatabase> | null = null;

/**
 * Cria as stores que faltam. Numa subida de versão, as existentes são apagadas antes: o conteúdo
 * pertence ao formato antigo e é reconstruído no próximo sync, que a limpeza do `__meta` dispara.
 */
const createStores = (db: IDBPDatabase, recriar: boolean): void => {
  for (const store of [...BF_SELLER_STORES, BF_SELLER_META_STORE]) {
    if (db.objectStoreNames.contains(store)) {
      if (!recriar) continue;
      db.deleteObjectStore(store);
    }
    // Chave fora da linha: vários payloads trazem `COD_LOJA` nulo, e `keyPath` embutido com
    // `null` rejeita a gravação. O writer calcula a chave e passa em `put(value, key)`.
    db.createObjectStore(store);
  }
};

/**
 * Quanto a abertura espera, depois de bloqueada, antes de desistir.
 *
 * O upgrade fica bloqueado enquanto outra aba mantém o banco aberto na versão antiga, e o `openDB`
 * não tem prazo: sem isso, a gravação do sync esperava para sempre e levava o sync junto.
 */
export const BF_SELLER_BLOCKED_TIMEOUT_MS = 10000;

export const openBfSellerDb = async (): Promise<IDBPDatabase> => {
  if (!connection) {
    let blockedTimer: ReturnType<typeof setTimeout> | undefined;
    let gaveUp = false;
    let rejectBlocked: (error: Error) => void = () => {};
    const blockedTooLong = new Promise<never>((_, reject) => {
      rejectBlocked = reject;
    });

    const opening = openDB(BF_SELLER_DB_NAME, BF_SELLER_DB_VERSION, {
      upgrade(db, oldVersion) {
        createStores(db, oldVersion > 0);
      },
      blocked() {
        console.warn('[BF_SELLER_DATASETS] upgrade bloqueado por outra aba aberta');
        if (blockedTimer) return;
        blockedTimer = setTimeout(() => {
          gaveUp = true;
          rejectBlocked(new Error('[BF_SELLER_DATASETS] upgrade bloqueado: feche as outras abas do app'));
        }, BF_SELLER_BLOCKED_TIMEOUT_MS);
      },
      terminated() {
        connection = null;
      },
    });

    // Desistimos, mas o pedido de abertura continua na fila do navegador: se ele sair depois,
    // fecha a conexão órfã para não ser ela a bloquear a próxima tentativa.
    opening.then((db) => gaveUp && db.close()).catch(() => {});

    connection = Promise.race([opening, blockedTooLong])
      .finally(() => clearTimeout(blockedTimer))
      .catch((error) => {
        connection = null;
        throw error;
      });

    const db = await connection;
    db.addEventListener('versionchange', () => {
      db.close();
      connection = null;
    });
  }

  return connection;
};

export const closeBfSellerDb = async (): Promise<void> => {
  if (!connection) return;
  try {
    const db = await connection;
    db.close();
  } catch {
    /* conexão já inválida */
  } finally {
    connection = null;
  }
};

export const deleteBfSellerDb = async (): Promise<void> => {
  await closeBfSellerDb();
  try {
    await deleteDB(BF_SELLER_DB_NAME, {
      blocked() {
        console.warn('[BF_SELLER_DATASETS] exclusão bloqueada até outras conexões fecharem');
      },
    });
  } catch (error) {
    console.error('[BF_SELLER_DATASETS] falha ao excluir o banco:', error);
  }
};
